import React from 'react';
import { AddCircle } from '@mui/icons-material';
import { array, boolean, object, string } from 'yup';
import { PanelMemberInfo } from './PanelMemberInfo';
import {
  FieldArray,
  type FieldArrayRenderProps,
  FormikProvider,
  useFormik,
} from 'formik';
import { type InterviewPanelMemberData } from './types';
import { Button, Checkbox, IconButton } from '../../../../../../components';
import { PAGE_TEXT } from '../../../../lang';
import { AssessmentAccordion } from '../../../../components';

type PanelInterviewBoxProps = {
  isViewMode: boolean;
  hideDeclaration: boolean;
  initialCheckbox: boolean;
  initialValues: InterviewPanelMemberData[];
  onJumpToNext?: () => void;
  handleSubmit: (
    interviewPanelListData: InterviewPanelMemberData[],
  ) => Promise<void>;
  refetchAssessment?: () => void;
};

const PANEL_TEXT = PAGE_TEXT.DRC_ASSESSMENT_DETAILS.PANEL_INFORMATION;
export const PanelInterviewBox: React.FunctionComponent<
  PanelInterviewBoxProps
> = (props: PanelInterviewBoxProps) => {
  const {
    initialValues,
    initialCheckbox,
    isViewMode,
    onJumpToNext,
    handleSubmit,
    hideDeclaration,
  } = props;

  const formik = useFormik<{
    confirm: boolean;
    interviewPanelMembers: InterviewPanelMemberData[];
  }>({
    initialValues: {
      confirm: initialCheckbox,
      interviewPanelMembers: initialValues,
    },
    validationSchema: object({
      confirm: boolean().required(),
      interviewPanelMembers: array().of(
        object({
          netId: string().required('Required'),
          givenName: string().required('Required'),
          surname: string().required('Required'),
          email: string().email('Invalid email format').required('Required'),
          department: string().required('Required'),
          post: string()
            .required('Required')
            .test(
              'byte-length',
              'Exceeds maximum length of 50 characters',
              value => {
                return value.length <= 50;
              },
            ),
          rank: string()
            .required('Required')
            .test(
              'byte-length',
              'Exceeds maximum length of 50 characters',
              value => {
                return value.length <= 50;
              },
            ),
          isPanelChair: boolean(),
        }),
      ),
    }),

    onSubmit: async val => {
      const { interviewPanelMembers } = val;
      await handleSubmit(interviewPanelMembers);
    },
  });
  const addMember =
    (arrayHelpers: FieldArrayRenderProps) =>
    async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      arrayHelpers.push({
        netId: '',
        title: '',
        jobTitle: '',
        givenName: '',
        surname: '',
        email: '',
        department: '',
        institution: '',
        isPanelChair: false,
        post: '',
        rank: '',
      });
    };

  const deleteMember =
    (arrayHelpers: FieldArrayRenderProps, index: number): (() => void) =>
    () => {
      arrayHelpers.remove(index);
    };

  return (
    <div className="pt-4 !px-0">
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit} noValidate>
          {!hideDeclaration && (
            <div className="px-6 mb-4">
              <Checkbox
                label={PANEL_TEXT.CONFIRM}
                value={formik.values.confirm}
                name="confirm"
                disabled={isViewMode}
                onChange={formik.handleChange}
              />
            </div>
          )}

          {formik.values.confirm && (
            <FieldArray
              name="interviewPanelMembers"
              render={(arrayHelpers: FieldArrayRenderProps) => (
                <>
                  {isViewMode ? (
                    <div>
                      <h2 className="font-bold">{PANEL_TEXT.PANEL_CHAIR}</h2>
                      <div className="h-[0.4rem] bg-[#951D1D] mt-2" />
                      <PanelMemberInfo
                        key={`interviewPanelMembers${0}`}
                        index={0}
                        isViewMode={isViewMode}
                        handleDeleteMember={deleteMember(arrayHelpers, 0)}
                      />
                      <div className="px-4">
                        <div className="h-[0.05em] bg-slate-500 mt-2" />
                      </div>
                    </div>
                  ) : (
                    <AssessmentAccordion
                      title={PANEL_TEXT.PANEL_CHAIR}
                      defaultExpanded
                      AssessDetails={
                        <div className="!bg-[#F6F6F6]">
                          <PanelMemberInfo
                            key={`interviewPanelMembers${0}`}
                            index={0}
                            isViewMode={isViewMode}
                            handleDeleteMember={deleteMember(arrayHelpers, 0)}
                          />
                        </div>
                      }
                    />
                  )}
                  {isViewMode ? (
                    <div className="mt-4">
                      <h2 className="font-bold">{PANEL_TEXT.PANEL_MEMBER}</h2>
                      <div className="h-[0.4rem] bg-[#951D1D] mt-2" />
                      {formik.values.interviewPanelMembers.map(
                        (item: InterviewPanelMemberData, index) => {
                          return (
                            index !== 0 && (
                              <div key={index}>
                                <PanelMemberInfo
                                  key={`interviewPanelMembers${index}`}
                                  index={index}
                                  isViewMode={isViewMode}
                                  handleDeleteMember={deleteMember(
                                    arrayHelpers,
                                    index,
                                  )}
                                />
                                <div className="px-4">
                                  <div className="h-[0.05em] bg-slate-500 mt-2" />
                                </div>
                              </div>
                            )
                          );
                        },
                      )}
                    </div>
                  ) : (
                    <AssessmentAccordion
                      title={PANEL_TEXT.PANEL_MEMBER}
                      defaultExpanded
                      AssessDetails={
                        <div className="!bg-[#F6F6F6]">
                          {formik.values.interviewPanelMembers.map(
                            (item: InterviewPanelMemberData, index) => {
                              return (
                                index !== 0 && (
                                  <PanelMemberInfo
                                    key={`interviewPanelMembers${index}`}
                                    index={index}
                                    isViewMode={isViewMode}
                                    handleDeleteMember={deleteMember(
                                      arrayHelpers,
                                      index,
                                    )}
                                  />
                                )
                              );
                            },
                          )}
                        </div>
                      }
                    />
                  )}

                  {!isViewMode && (
                    <div className="w-full flex justify-center">
                      <IconButton
                        size="large"
                        icon={<AddCircle color="primary" />}
                        onClick={addMember(arrayHelpers)}
                      />
                    </div>
                  )}
                </>
              )}
            />
          )}
          {!isViewMode && (
            <div className="w-full flex justify-center py-8 gap-4">
              {formik.values.confirm && (
                <Button variant="contained" color="primary" type="submit">
                  {PAGE_TEXT.SAVE}
                </Button>
              )}
              {onJumpToNext && (
                <Button onClick={onJumpToNext}>{PAGE_TEXT.NEXT}</Button>
              )}
            </div>
          )}
        </form>
      </FormikProvider>
    </div>
  );
};
